import React, { useState, useMemo } from "react";
import { ChevronsUpDown, ChevronLeft, ChevronRight, Check } from "lucide-react";

// Mock FormatDateTime function
const FormatDateTime = (dateString) => {
  return dateString;
};

// Lot size configuration
const LOT_SIZE_CONFIG = {
  NIFTY: 75,
  BANKNIFTY: 30
};

// Helper function to get lot size based on symbol
const getLotSize = (symbol) => {
  const upperSymbol = symbol.toUpperCase();
  if (upperSymbol.includes('BANKNIFTY')) {
    return LOT_SIZE_CONFIG.BANKNIFTY; // BANKNIFTY takes precedence
  } else if (upperSymbol.includes('NIFTY')) {
    return LOT_SIZE_CONFIG.NIFTY;
  }
  return null; // Stock option
};

// Helper function to check if symbol is index option
const isIndexOption = (symbol) => {
  const upperSymbol = symbol.toUpperCase();
  return upperSymbol.includes('NIFTY') || upperSymbol.includes('BANKNIFTY');
};

export default function ClosePositionsTable({
  closePositionsData,
  filteredClosePositionsData = [],
  setFilteredClosePositionsFno,
  setFilteredClosePositionsEquity,
  sortOrder,
  setSortOrder,
  currentState,
  accountName = [], // This is the accountNames prop
  allUser = [], // Keeping this for backward compatibility
  currentPage,
  setCurrentPage,
  ...props
}) {
  // Use accountName as the primary source, fallback to allUser for backward compatibility
  const accountNames = accountName.length > 0 ? accountName : allUser;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  console.log("AccountNames data:", accountNames);
  console.log("Close position symbol detection test:", filteredClosePositionsData.map(position => ({
    symbol: position.symbol,
    isIndex: isIndexOption(position.symbol),
    lotSize: getLotSize(position.symbol)
  })));

  // Group close positions by symbol + strategy + side
  const groupedPositions = useMemo(() => {
    const groups = {};
    
    filteredClosePositionsData.forEach(position => {
      const key = `${position.symbol}-${position.strategy || 'default'}-${position.side}`;
      
      if (!groups[key]) {
        groups[key] = {
          symbol: position.symbol,
          strategy: position.strategy || 'default',
          side: position.side,
          netAvg: position.netAvg,
          ltp: position.ltp,
          realized_profit: position.realized_profit,
          unrealized_profit: position.unrealized_profit,
          pl: position.pl,
          actualQuantities: {}, // Store actual quantities from positions
          clientPositionMap: {} // Map client to their actual quantities
        };
      }
      
      // Store actual quantities and client mappings
      position.clientIds.forEach(clientId => {
        if (!groups[key].clientPositionMap[clientId]) {
          groups[key].clientPositionMap[clientId] = [];
        }
        groups[key].clientPositionMap[clientId].push(position.netQty);
      });
      
      if (!groups[key].actualQuantities[position.netQty]) {
        groups[key].actualQuantities[position.netQty] = [];
      }
      groups[key].actualQuantities[position.netQty].push(...position.clientIds);
    });
    
    // Remove duplicates from clientPositionMap
    Object.values(groups).forEach(group => {
      Object.keys(group.clientPositionMap).forEach(clientId => {
        group.clientPositionMap[clientId] = [...new Set(group.clientPositionMap[clientId])];
      });
    });
    
    return Object.values(groups);
  }, [filteredClosePositionsData]);

  // Generate multiplier tiers for each group
  const enhancedGroupedPositions = useMemo(() => {
    return groupedPositions.map(group => {
      const isIndex = isIndexOption(group.symbol);
      
      if (isIndex) {
        // For index options, use multiplier logic
        const lotSize = getLotSize(group.symbol);
        
        // Get unique multipliers from accountNames
        const uniqueMultipliers = [...new Set(
          accountNames
            .map(user => user.index_multiplier)
            .filter(multiplier => multiplier > 0)
        )].sort((a, b) => a - b);
        
        console.log("Unique multipliers for close position", group.symbol, ":", uniqueMultipliers);
        
        const multiplierTiers = uniqueMultipliers.map(multiplier => {
          const calculatedQty = multiplier * lotSize;
          
          // Get clients who actually have this quantity
          const clientsWithThisQty = group.actualQuantities[calculatedQty] || [];
          
          return {
            multiplier,
            quantity: calculatedQty,
            allClients: accountNames.filter(user => user.index_multiplier === multiplier),
            clientsWithPosition: clientsWithThisQty
          };
        });
        
        return {
          ...group,
          multiplierTiers,
          isIndexOption: true
        };
      } else {
        // For stock options, keep current logic
        const quantityTiers = Object.entries(group.actualQuantities)
          .map(([qty, clientIds]) => ({
            quantity: parseInt(qty),
            clientIds: [...new Set(clientIds)]
          }))
          .sort((a, b) => a.quantity - b.quantity);
          
        return {
          ...group,
          quantityTiers,
          isIndexOption: false
        };
      }
    });
  }, [groupedPositions, accountNames]);

  console.log("Enhanced grouped close positions:", enhancedGroupedPositions);

  // Sorting logic
  const sortedPositions = useMemo(() => {
    if (!sortConfig.key) return enhancedGroupedPositions;
    
    return [...enhancedGroupedPositions].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (sortConfig.key === 'symbol') {
        aValue = aValue.replace("NSE:", "");
        bValue = bValue.replace("NSE:", "");
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [enhancedGroupedPositions, sortConfig]);

  // Pagination
  const itemsPerPage = 10;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedPositions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedPositions.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };

  const getSideColor = (side) => {
    return side === 1 ? "text-green-400" : "text-red-400";
  };

  const getPLColor = (pl) => {
    const plValue = parseFloat(pl);
    if (plValue > 0) return "text-green-400";
    if (plValue < 0) return "text-red-400";
    return "text-white";
  };

  if (!filteredClosePositionsData || filteredClosePositionsData.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
        <div className="text-lg font-medium">No Close Position Found!!</div>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100vh-120px)] bg-zinc-900 flex flex-col">
      {/* Sticky Header */}
      <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
        <div className="grid grid-cols-8 gap-4 px-6 py-4 text-sm font-medium text-zinc-300">
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('symbol')}
          >
            <span>Symbol</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('side')}
          >
            <span>Buy/Sell</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <div className="text-left">Net Qty</div>
          <div className="text-left">Avg Price</div>
          <div className="text-left">LTP</div>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('realized_profit')}
          >
            <span>Realized P&L</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('unrealized_profit')}
          >
            <span>Unrealized P&L</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('pl')}
          >
            <span>Total P&L</span>
            <ChevronsUpDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Scrollable Table Body */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="min-w-full bg-zinc-900">
          <div className="divide-y divide-zinc-700">
            {currentItems.map((position, index) => (
              <div key={index} className="bg-zinc-900">
                {/* Main Position Row */}
                <div className="grid grid-cols-8 gap-4 px-6 py-4 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
                  <div className="font-medium">
                    {position.symbol.replace("NSE:", "")}
                  </div>
                  <div className={`font-medium ${getSideColor(position.side)}`}>
                    {position.side === 1 ? "BUY" : "SELL"}
                  </div>
                  <div>{position.isIndexOption ? 
                    position.multiplierTiers?.reduce((sum, tier) => sum + (tier.allClients.length * tier.quantity), 0) :
                    position.quantityTiers?.reduce((sum, tier) => sum + tier.quantity, 0)
                  }</div>
                  <div>₹{position.netAvg?.toFixed(2)}</div>
                  <div>₹{position.ltp !== 'N/A' ? position.ltp?.toFixed(2) : 'N/A'}</div>
                  <div className={`font-medium ${getPLColor(position.realized_profit)}`}>
                    ₹{parseFloat(position.realized_profit)?.toFixed(2)}
                  </div>
                  <div className={`font-medium ${getPLColor(position.unrealized_profit)}`}>
                    ₹{parseFloat(position.unrealized_profit)?.toFixed(2)}
                  </div>
                  <div className={`font-medium ${getPLColor(position.pl)}`}>
                    ₹{parseFloat(position.pl)?.toFixed(2)}
                  </div>
                </div>

                {/* Enhanced Quantity-Client Sub Table */}
                <div className="px-6 pb-4">
                  <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
                    {position.isIndexOption ? (
                      // Index Options - Multiplier Logic
                      <>
                        <div className="bg-zinc-700 px-4 py-2">
                          <div className="grid grid-cols-3 gap-4 text-xs font-medium text-zinc-300">
                            <div>Multiplier</div>
                            <div>Quantity</div>
                            <div>Clients</div>
                          </div>
                        </div>
                        
                        <div className="divide-y divide-zinc-700">
                          {position.multiplierTiers.map((tier, tierIndex) => (
                            <div key={tierIndex} className="grid grid-cols-[120px,120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                              <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                {tier.multiplier}
                              </div>
                              <div className="font-medium text-emerald-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                {tier.quantity}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {tier.allClients.map((user, userIndex) => {
                                  const hasPosition = tier.clientsWithPosition.includes(user.client_id);
                                  return (
                                    <span 
                                      key={userIndex}
                                      className={`text-zinc-300  rounded px-2 py-1 text-xs flex items-center space-x-1 ${
                                        hasPosition ? 'bg-green-900 border border-green-500/30' : ' bg-zinc-700'
                                      }`}
                                    >
                                      {hasPosition && (
                                        <Check className="w-3 h-3 text-white" />
                                      )}
                                      <span>{user.name}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      // Stock Options - Current Logic
                      <>
                        <div className="bg-zinc-700 px-4 py-2">
                          <div className="grid grid-cols-2 gap-4 text-xs font-medium text-zinc-300">
                            <div>Quantity</div>
                            <div>Clients</div>
                          </div>
                        </div>
                        
                        <div className="divide-y divide-zinc-700">
                          {position.quantityTiers.map((tier, tierIndex) => (
                            <div key={tierIndex} className="grid grid-cols-[120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                              <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                {tier.quantity}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {accountNames
                                  .filter(user => tier.clientIds.includes(user.client_id))
                                  .map((user, userIndex) => (
                                    <span 
                                      key={userIndex}
                                      className="text-zinc-300 bg-zinc-700 rounded px-2 py-1 text-xs flex items-center space-x-1"
                                    >
                                      <Check className="w-3 h-3 text-green-400" />
                                      <span>{user.name}</span>
                                    </span>
                                  ))
                                }
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fixed Pagination at Bottom */}
      <div className="bg-zinc-800 border-t border-zinc-700 shrink-0 sticky bottom-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="text-sm text-zinc-400">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedPositions.length)} of {sortedPositions.length} close positions
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </button>
            
            <div className="flex space-x-1">
              {getPageNumbers().map(number => (
                <button
                  key={number}
                  onClick={() => handlePageChange(number)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    currentPage === number
                      ? 'bg-blue-600 text-white'
                      : 'text-zinc-300 bg-zinc-700 hover:bg-zinc-600'
                  }`}
                >
                  {number}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center px-3 py-2 text-sm font-medium text-zinc-300 bg-zinc-700 rounded-lg hover:bg-zinc-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}